import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import type { Command } from '@atlaskit/editor-common/types';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ListBulletedIcon } from '@atlaskit/editor-toolbar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import ChildWorkItemsIcon from '@atlaskit/icon/core/child-work-items';
import ClockIcon from '@atlaskit/icon/core/clock';
import DecisionIcon from '@atlaskit/icon/core/decision';
import ListChecklistIcon from '@atlaskit/icon/core/list-checklist';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import type { JsonLd } from '@atlaskit/json-ld-types';
import type { SmartLinkResponse } from '@atlaskit/linking-types';

import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../../types/types';
import { summarizeSmartlink } from '../../prebuilt/config-items/prompts/summarize-smartlink/summarize-smartlink';

export type SmartLinkActionKey =
	| 'listKeyTopics'
	| 'listKeyThemes'
	| 'extractInsights'
	| 'extractUserQuotes'
	| 'addDecisions'
	| 'defineActionItems'
	| 'summarizeChanges'
	| 'addRelatedWorkItems'
	| 'listTodos';

export type SmartLinkDropdownItem = {
	icon: React.ReactNode;
	label: string;
	onClick: () => void;
	testId: string;
};

const actionLabels = {
	listKeyTopics: aiMessages.smartlinkListKeyTopics,
	listKeyThemes: aiMessages.smartlinkListKeyThemes,
	extractInsights: aiMessages.smartlinkExtractInsights,
	extractUserQuotes: aiMessages.smartlinkExtractUserQuotes,
	addDecisions: aiMessages.smartlinkAddDecisions,
	defineActionItems: aiMessages.smartlinkDefineActionItems,
	summarizeChanges: aiMessages.smartlinkSummarizeChanges,
	addRelatedWorkItems: aiMessages.smartlinkAddRelatedWorkItems,
	listTodos: aiMessages.smartlinkListTodos,
} satisfies Record<SmartLinkActionKey, MessageDescriptor>;

/**
 * Determines which AI actions are available for a given SmartLink based on its provider
 * and content type.
 *
 * using similar detection strategy as server-side rendering of actions in SmartLinkCard,
 * which is based on provider and content type metadata returned by the Smart Link Resolver.
 * see `platform/packages/linking-platform/smart-card/src/state/helpers.ts`
 *
 * Detection strategy:
 * - `details.meta.key` is the primary discriminator for all known providers.
 * - `details.meta.resourceType` differentiates Confluence sub-types (page, whiteboard, database).
 * - `details.data['@type']` differentiates Google Workspace sub-types (Docs, Sheets, Slides).
 * - `details.data['schema:fileFormat']` (MIME type) differentiates Microsoft 365 sub-types.
 * - `details.data.generator.name` is the fallback for providers without a confirmed meta.key
 *   in the frontend codebase (Zoom, MS Teams, Google Chat, Mural, Notion, Smartsheet).
 *
 * Returns an empty array for unknown or unsupported providers
 */
export const getActionsForSmartLink = (details?: SmartLinkResponse): SmartLinkActionKey[] => {
	// `meta` has a [k: string]: any index signature, so direct access is safe without casting.
	const metaKey = details?.meta?.key as string | undefined;
	const resourceType = details?.meta?.resourceType as string | undefined;
	const metaProduct = details?.meta?.product as string | undefined;

	// Cast to BaseData (the common base for non-collection responses) to access typed properties.
	const data = details?.data as JsonLd.Data.BaseData | undefined;
	const dataType = data?.['@type'];
	// schema:fileFormat only exists on Document, which extends BaseData.
	const fileFormat = (data as JsonLd.Data.Document | undefined)?.['schema:fileFormat'];
	// generator is typed as Primitives.Object | Primitives.Link on BaseData.
	// Primitives.Link can be a string or LinkModel (which has href). Only Object has name.
	const generator = data?.generator;
	const generatorName =
		generator && typeof generator !== 'string' && !('href' in generator)
			? generator.name
			: undefined;

	const hasType = (type: JsonLd.Primitives.ObjectType): boolean =>
		Array.isArray(dataType) ? dataType.includes(type) : dataType === type;

	// all Confluence content types share meta.product === 'confluence'
	if (metaProduct === 'confluence') {
		if (resourceType === 'whiteboard') {
			return ['listKeyThemes', 'addDecisions', 'defineActionItems'];
		}
		if (resourceType === 'database') {
			return ['extractInsights'];
		}
		// page, live-page, blog, and any other Confluence page-like resource types
		return ['summarizeChanges', 'listKeyTopics'];
	}

	switch (metaKey) {
		case 'jira-object-provider':
			return ['addRelatedWorkItems'];

		case 'loom-object-provider':
			return ['extractUserQuotes', 'addDecisions', 'defineActionItems'];

		case 'trello-object-provider':
			return ['listTodos'];

		case 'miro-object-provider':
			return ['listKeyThemes'];

		case 'google-object-provider':
			if (hasType('schema:SpreadsheetDigitalDocument')) {
				return ['extractInsights'];
			}
			// TextDigitalDocument (Google Docs) and other Google doc types
			return ['listKeyTopics'];

		case 'onedrive-object-provider':
		case 'sharepoint-object-provider':
			if (fileFormat?.includes('spreadsheetml') || fileFormat?.includes('ms-excel')) {
				return ['extractInsights'];
			}
			// wordprocessingml, ms-word, presentationml, and other Office file types
			return ['listKeyTopics'];

		case 'figma-object-provider':
			return ['summarizeChanges'];

		case 'slack-object-provider':
			return ['addDecisions', 'defineActionItems'];

		default:
			// fallback: use generator.name for providers without a confirmed meta.key.
			if (generatorName === 'Zoom') {
				return ['extractUserQuotes', 'addDecisions', 'defineActionItems'];
			}
			if (generatorName === 'Microsoft Teams' || generatorName === 'Google Chat') {
				return ['addDecisions', 'defineActionItems'];
			}
			if (generatorName === 'MURAL') {
				return ['listKeyThemes'];
			}
			if (generatorName === 'Notion') {
				return ['listKeyTopics'];
			}
			if (generatorName === 'Smartsheet') {
				return ['extractInsights'];
			}
			return [];
	}
};

// icon render functions for each smartlink action key
const ACTION_KEY_ICONS: Record<SmartLinkActionKey, () => React.ReactNode> = {
	listKeyTopics: () => <ListBulletedIcon label="" size="small" />,
	listKeyThemes: () => <ListBulletedIcon label="" size="small" />,
	extractInsights: () => <ChartTrendUpIcon label="" size="small" />,
	extractUserQuotes: () => <QuotationMarkIcon label="" size="small" />,
	addDecisions: () => <DecisionIcon label="" size="small" />,
	defineActionItems: () => <ListChecklistIcon label="" size="small" />,
	summarizeChanges: () => <ClockIcon label="" size="small" />,
	addRelatedWorkItems: () => <ChildWorkItemsIcon label="" size="small" />,
	listTodos: () => <ListChecklistIcon label="" size="small" />,
};

export const getSmartLinkDropdownItems = ({
	aiGlobalOptIn,
	details,
	intl,
	nodeType,
	smartlinkUrl,
	testIdPrefix,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	details?: SmartLinkResponse;
	intl: IntlShape;
	nodeType: NodeType;
	smartlinkUrl: string;
	testIdPrefix: string;
	view: EditorView;
}): SmartLinkDropdownItem[] => {
	const dispatchCommand = (cmd: Command) => {
		cmd(view.state, view.dispatch, view);
	};

	// temporary using `summarize` handler;
	// it will be replaced with per-action handlers once actions are implemented.
	const summarize = () => {
		const openAIModalCommand = createOpenAIModalCommand({
			state: view.state,
			configItem: summarizeSmartlink({ smartlinkUrl }), // this will be dynamic based on the action
			lastTriggeredFrom: 'floatingToolbar',
			triggerMethod: 'click',
			aiGlobalOptIn,
			locale: intl.locale,
			triggeredFor: nodeType,
		});
		dispatchCommand(openAIModalCommand);
	};

	const actionKeys = getActionsForSmartLink(details);

	// render action items for known providers; return empty for unknown ones.
	if (actionKeys.length > 0) {
		return actionKeys.map((actionKey) => ({
			icon: ACTION_KEY_ICONS[actionKey](),
			label: intl.formatMessage(actionLabels[actionKey]),
			testId: `${testIdPrefix}-smartlink-dropdown-${actionKey}`,
			onClick: summarize,
		}));
	}

	// in future, if we decide to add generic actions for unknown providers, we can add them here
	return [];
};
